// src/components/ProfileMobile.jsx
import { useContext, useEffect, useState, useRef } from "react";
import {
  Box,
  Avatar,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Autocomplete,
  Chip,
  Slide,
  Modal,
  Menu,
  ListItemText,
  useMediaQuery,
} from "@mui/material";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import { ToastContainer } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import { LanguageContext } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import {
  doc,
  onSnapshot,
  collection,
  query,
  where,
  updateDoc,
  getDoc,
  getDocs,
  setDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  getStorage,
  uploadBytes,
  ref as storageRef,
  getDownloadURL,
} from "firebase/storage";
import { db } from "../firebase";
import { getApp } from "firebase/app";
import {
  getCurrentUserSubscriptions,
  getStripePayments,
} from "@invertase/firestore-stripe-payments";
import MobileHeader from "./headers/MobileHearder";
import { showCustomToast } from "../misc/CustomToast";
import { ThemeProvider, createTheme, useTheme } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import PrivacyPolicy from "./ProvacyPolicy";

const appStripe = getApp();
const paymentsStripe = getStripePayments(appStripe, {
  productsCollection: "products",
  customersCollection: "customers",
});

export default function ProfileMobile() {
  const { translate } = useContext(LanguageContext);
  const { currentUser, logOut } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [role, setRole] = useState("Normal User");
  const [profile, setProfile] = useState(null);
  const [members, setMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [editNameMode, setEditNameMode] = useState(false);
  const [newName, setNewName] = useState("");
  const [nameLoading, setNameLoading] = useState(false);
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("Normal User");
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [, setAdminEmails] = useState([]);
  const [openAddProjModal, setOpenAddProjModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newPageListCategories] = useState([]);
  const [selectedNewProjMembers, setSelectedNewProjMembers] = useState([]);
  const [openEditProjModal, setOpenEditProjModal] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState(null);
  const [editedProjMembers, setEditedProjMembers] = useState([]);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(false);
  const [userToDelete] = useState(null);
  const ROLES = ["Normal User", "Admin", "Suspended"];
  const functions = getFunctions();
  const sendInviteEmail = httpsCallable(functions, "sendInviteEmail");
  const [notifications, setNotifications] = useState([]);
  const [anchorNotif, setAnchorNotif] = useState(null);
  const [anchorProfile, setAnchorProfile] = useState(null);
  const handleBellClose = () => setAnchorNotif(null);
  const handleProfileClose = () => setAnchorProfile(null);
  const [isPremium, setIsPremium] = useState(false);
  const canCreateAnotherProject = isPremium || projects.length < 1;
  const MAX_FREE_COLLABS = 3;
  const FREE_PROJECT_LIMIT = 1;
  const FREE_TICKET_LIMIT = 50;
  const sendContactMessage = httpsCallable(functions, "sendContactMessage");
  const [contactOpen, setContactOpen] = useState(false);
  const [contactEmail, setContactEmail] = useState(currentUser?.email || "");
  const [contactSubject, setContactSubject] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactLoading, setContactLoading] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const theme = useTheme();
  const fullScreenPrivacy = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (currentUser?.email) setContactEmail(currentUser.email);
  }, [currentUser]);

  const [darkMode, setDarkMode] = useState(false);

  const lightTheme = createTheme({
    palette: {
      mode: "light",
      background: { default: "#f4f8f9", paper: "#fff" },
    },
  });

  const darkTheme = createTheme({
    palette: {
      mode: "dark",
      background: { default: "#121212", paper: "#1e1e1e" },
      text: { primary: "#fff", secondary: "#ccc" },
    },
  });

  useEffect(() => {
    if (!currentUser) return;
    const ref = doc(db, "profilsUsers", currentUser.uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      setRole(data.role);
      setProfile({ id: snap.id, ...data });
      if (data.darkMode !== undefined) {
        setDarkMode(data.darkMode);
      }
    });
    return unsub;
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    getCurrentUserSubscriptions(paymentsStripe, { status: "active" })
      .then((subs) => setIsPremium(subs.length > 0))
      .catch(console.error);
  }, [currentUser]);

  useEffect(() => {
    if (!profile?.companyId) return;
    const compRef = doc(db, "companies", profile.companyId);
    const unsub = onSnapshot(compRef, (snap) => {
      setPendingInvites(snap.data()?.invitedEmails || []);
    });
    return unsub;
  }, [profile]);

  useEffect(() => {
    if (!profile?.companyId) return;
    setLoadingMembers(true);
    const q = query(
      collection(db, "profilsUsers"),
      where("companyId", "==", profile.companyId)
    );
    const unsub = onSnapshot(q, (snap) => {
      setMembers(
        snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((u) => u.id !== currentUser.uid)
      );
      setLoadingMembers(false);
    });
    return unsub;
  }, [profile, currentUser]);

  useEffect(() => {
    if (!profile?.companyId) return;
    setLoadingProjects(true);
    const q = query(
      collection(db, "projects"),
      where("companyId", "==", profile.companyId)
    );
    const unsub = onSnapshot(q, (snap) => {
      setProjects(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoadingProjects(false);
    });
    return unsub;
  }, [profile]);

  useEffect(() => {
    if (!profile?.companyId) return;
    const q = query(
      collection(db, "profilsUsers"),
      where("companyId", "==", profile.companyId),
      where("role", "==", "Admin")
    );
    getDocs(q).then((snap) =>
      setAdminEmails(snap.docs.map((d) => d.data().email))
    );
  }, [profile]);

  useEffect(() => {
    if (!currentUser) return;
    const notifCol = collection(
      db,
      "profilsUsers",
      currentUser.uid,
      "notifications"
    );
    const unsub = onSnapshot(notifCol, (snap) =>
      setNotifications(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [currentUser]);

  const saveName = async () => {
    setNameLoading(true);
    await updateDoc(doc(db, "profilsUsers", currentUser.uid), {
      name: newName,
    });
    setNameLoading(false);
    setEditNameMode(false);
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = URL.createObjectURL(file);
    });
    const ratio = Math.min(1, 100 / img.width);
    const w = Math.round(img.width * ratio);
    const h = Math.round(img.height * ratio);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise((res) =>
      canvas.toBlob(res, file.type || "image/jpeg", 0.8)
    );
    const storage = getStorage();
    const path = `profilePhotos/${currentUser.uid}/${file.name}`;
    const imgRef = storageRef(storage, path);
    await uploadBytes(imgRef, blob);
    const url = await getDownloadURL(imgRef);
    await updateDoc(doc(db, "profilsUsers", currentUser.uid), {
      photoUrl: url,
    });
  };

  const handleSendInvite = async () => {
    if (!inviteEmail.match(/\S+@\S+\.\S+/)) return;
    setInviteLoading(true);
    const compRef = doc(db, "companies", profile.companyId);
    const compSnap = await getDoc(compRef);
    if (compSnap.data()?.invitedEmails?.includes(inviteEmail)) {
      setInviteLoading(false);
      return;
    }
    const qCheck = query(
      collection(db, "profilsUsers"),
      where("email", "==", inviteEmail)
    );
    const snap = await getDocs(qCheck);
    if (!snap.empty && snap.docs[0].data().companyId) {
      setInviteLoading(false);
      return;
    }
    await sendInviteEmail({
      email: inviteEmail,
      companyId: profile.companyId,
      invitedBy: currentUser.email,
      role: inviteRole,
      message: inviteMessage,
    });
    await updateDoc(compRef, { invitedEmails: arrayUnion(inviteEmail) });
    setOpenInviteDialog(false);
    setInviteLoading(false);
  };

  const handleCreateProject = async () => {
    if (!isPremium && projects.length >= 1) {
      showCustomToast(translate("PremiumNeededForMoreProjects"), "error");
      return;
    }
    if (!isPremium && selectedNewProjMembers.length >= 3) {
      showCustomToast(translate("Limit3CollaboratorsNonPremium"), "error");
      return;
    }
    if (!newProjectName.trim()) return;
    const id = newProjectName.trim();
    const refProj = doc(db, "projects", id);
    const snap = await getDoc(refProj);
    if (snap.exists()) return;
    await setDoc(refProj, {
      name: id,
      companyId: profile.companyId,
      companyName: profile.companyName,
      pageListCategories: newPageListCategories,
      createdAt: new Date(),
      memberIds: [currentUser.uid, ...selectedNewProjMembers.map((u) => u.id)],
      memberEmails: [
        currentUser.email,
        ...selectedNewProjMembers.map((u) => u.email),
      ],
    });
    setOpenAddProjModal(false);
  };

  const handleEditProject = async () => {
    // 🔒 Non-premium: max 3 collaborateurs (hors currentUser)
    if (!isPremium && editedProjMembers.length > 3) {
      showCustomToast(translate("Limit3CollaboratorsNonPremium"), "error");
      return;
    }

    const ref = doc(db, "projects", projectToEdit.id);
    await updateDoc(ref, {
      memberIds: editedProjMembers.map((u) => u.id),
      memberEmails: editedProjMembers.map((u) => u.email),
    });
    setOpenEditProjModal(false);
  };

  const handleDeleteUser = async () => {
    await updateDoc(doc(db, "profilsUsers", userToDelete.id), {
      companyId: null,
    });
    const q = query(
      collection(db, "projects"),
      where("companyId", "==", profile.companyId)
    );
    const snap = await getDocs(q);
    for (let p of snap.docs) {
      await updateDoc(doc(db, "projects", p.id), {
        memberIds: arrayRemove(userToDelete.id),
        memberEmails: arrayRemove(userToDelete.email),
      });
    }
    setConfirmDeleteUser(false);
  };

  return (
    // <Box sx={{ minHeight: "100vh", bgcolor: "#f4f8f9" }}>
    <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          color: "text.primary",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 1,
            bgcolor: "background.paper",
            color: "text.primary",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <MobileHeader
            title={translate("UserProfile")}
            showProfileItem={false}
          />
        </Box>

        <Menu
          anchorEl={anchorNotif}
          open={Boolean(anchorNotif)}
          onClose={handleBellClose}
          PaperProps={{ style: { maxHeight: "50vh", width: 280 } }}
        >
          {notifications.length === 0 ? (
            <MenuItem disabled>
              <ListItemText primary={translate("NoNotifications")} />
            </MenuItem>
          ) : (
            notifications.map((n) => (
              <MenuItem
                key={n.id}
                onClick={async () => {
                  await updateDoc(
                    doc(
                      db,
                      "profilsUsers",
                      currentUser.uid,
                      "notifications",
                      n.id
                    ),
                    { read: true }
                  );
                  handleBellClose();
                  // optionally navigate if your n.link or n.projectId/n.ticketId
                }}
              >
                <ListItemText
                  primary={n.message}
                  secondary={new Date(
                    n.timestamp.seconds * 1000
                  ).toLocaleString()}
                />
              </MenuItem>
            ))
          )}
        </Menu>

        <Menu
          anchorEl={anchorProfile}
          open={Boolean(anchorProfile)}
          onClose={handleProfileClose}
        >
          <MenuItem
            onClick={() => {
              handleProfileClose();
              navigate("/app/gotopremium");
            }}
          >
            <ListItemText primary={translate("GoPremium")} />
          </MenuItem>
          <MenuItem
            onClick={async () => {
              handleProfileClose();
              await logOut();
              navigate("/login");
            }}
          >
            <ListItemText primary={translate("Logout")} />
          </MenuItem>
        </Menu>

        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              bgcolor: "background.paper",
              p: 2,
              borderRadius: 2,
              mb: 2,
            }}
          >
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handlePhotoChange}
            />
            <Avatar
              src={profile?.photoUrl}
              sx={{ width: 80, height: 80, mb: 1 }}
              onClick={() => fileInputRef.current.click()}
            />
            {editNameMode ? (
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  size="small"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
                <Button size="small" onClick={saveName} disabled={nameLoading}>
                  {nameLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    translate("Save")
                  )}
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6">{profile?.name}</Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setNewName(profile?.name);
                    setEditNameMode(true);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            <Typography variant="body2">{profile?.email}</Typography>

            {isPremium && (
              <Chip
                label={translate("YouArePremium")}
                size="small"
                sx={{
                  mt: 1,
                  bgcolor: "gold",
                  color: "black",
                  fontWeight: 600,
                  // Une légère ombre pour que ça brille
                  boxShadow: "0 0 8px gold",
                  // Animation de "pulsation" de l'ombre
                  animation: "shimmer 2s infinite",
                  "@keyframes shimmer": {
                    "0%": { boxShadow: "0 0 5px gold" },
                    "50%": { boxShadow: "0 0 20px gold" },
                    "100%": { boxShadow: "0 0 5px gold" },
                  },
                }}
              />
            )}
            <Box
              sx={{
                mt: 1,
                p: 0.5,
                px: 1,
                bgcolor:
                  role === "Admin"
                    ? "error.main"
                    : role === "Normal User"
                    ? "primary.main"
                    : "grey.500",
                color: "white",
                borderRadius: "20%",
              }}
            >
              <Typography variant="caption">{translate(role)}</Typography>
            </Box>
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                borderRadius: 3,
                background:
                  "linear-gradient(135deg, rgba(64,123,255,0.14), rgba(93,210,255,0.12))",
                border: "1px solid",
                borderColor: "divider",
                boxShadow:
                  "0 10px 30px rgba(64,123,255,0.10), inset 0 1px 0 rgba(255,255,255,0.08)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Chip
                label={translate(role)}
                size="small"
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  fontWeight: 700,
                  px: 1.5,
                  bgcolor: isPremium
                    ? "gold"
                    : role === "Admin"
                    ? "#d32f2f"
                    : "primary.main",
                  color: isPremium
                    ? "#1a1a1a"
                    : role === "Admin"
                    ? "#fff"
                    : "primary.contrastText",
                  boxShadow: isPremium
                    ? "0 0 12px rgba(255,215,0,0.6)"
                    : role === "Admin"
                    ? "0 6px 18px rgba(211,47,47,0.35)"
                    : "0 6px 18px rgba(64,123,255,0.28)",
                }}
              />
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ fontWeight: 700, textAlign: "center" }}
              >
                {isPremium
                  ? "Premium — projets illimités, tickets illimités, collaborateurs illimités."
                  : `Plan gratuit — ${FREE_PROJECT_LIMIT} projet, ${FREE_TICKET_LIMIT} tickets/mois/projet, ${MAX_FREE_COLLABS} collaborateurs.`}
              </Typography>
              {!isPremium && (
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  <Chip
                    label="Limites du plan gratuit"
                    size="small"
                    color="warning"
                    sx={{ fontWeight: 600 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => navigate("/app/gotopremium")}
                    sx={{
                      borderRadius: 999,
                      px: 2,
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    Passer Premium
                  </Button>
                </Box>
              )}
            </Box>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={darkMode}
                  onChange={async (e) => {
                    const value = e.target.checked;
                    setDarkMode(value);
                    try {
                      await updateDoc(
                        doc(db, "profilsUsers", currentUser.uid),
                        {
                          darkMode: value,
                        }
                      );
                    } catch (err) {
                      console.error("DarkMode save error:", err);
                      showCustomToast(translate("DarkModeSaveFailed"), "error");
                    }
                  }}
                  color="primary"
                />
              }
              label={
                darkMode ? translate("DarkModeOn") : translate("DarkModeOff")
              }
            />
            <Button
              variant="text"
              size="small"
              onClick={() => setPrivacyOpen(true)}
              sx={{ mt: 1 }}
            >
              {translate("PrivacyPolicy") || "Privacy Policy"}
            </Button>
          </Box>

          {/* <Box sx={{ mb: 2, bgcolor: "white", p: 2, borderRadius: 2 }}> */}
          <Box
            sx={{ mb: 2, bgcolor: "background.paper", p: 2, borderRadius: 2 }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                {translate("TeamMembers")}{" "}
                <span
                  style={{
                    color:
                      members.length >= (isPremium ? Infinity : MAX_FREE_COLLABS)
                        ? "#d32f2f"
                        : "#2e7d32",
                    fontWeight: 700,
                  }}
                >
                  ({members.length}/{isPremium ? "∞" : MAX_FREE_COLLABS})
                </span>
                {!isPremium && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => navigate("/app/gotopremium")}
                    sx={{
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 700,
                      px: 1.5,
                      minHeight: "28px",
                    }}
                  >
                    Passer Premium
                  </Button>
                )}
              </Typography>
              {/* {role === "Admin" && (
              <Button
                size="small"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => setOpenInviteDialog(true)}
              >
                {translate("InviteUser")}
              </Button>
            )} */}
              {role === "Admin" && (
                <Button
                  size="small"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={() => {
                    if (!isPremium && members.length >= MAX_FREE_COLLABS) {
                      showCustomToast(
                        translate("Limit3CollaboratorsReachedInvite", {
                          limit: MAX_FREE_COLLABS,
                        }),
                        "error"
                      );
                      return;
                    }
                    setOpenInviteDialog(true);
                  }}
                >
                  {translate("InviteUser")}
                </Button>
              )}
            </Box>
            {loadingMembers ? (
              <Typography>{translate("Loading")}…</Typography>
            ) : (
              members.map((u) => (
                <Box
                  key={u.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    py: 1,
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  <Avatar
                    src={u.photoUrl}
                    sx={{ width: 32, height: 32, mr: 1 }}
                  />
                  <Typography sx={{ flexGrow: 1 }}>{u.name}</Typography>
                  {role === "Admin" && (
                    <FormControl size="small">
                      <Select
                        value={u.role}
                        onChange={(e) =>
                          updateDoc(doc(db, "profilsUsers", u.id), {
                            role: e.target.value,
                          })
                        }
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {ROLES.map((r) => (
                          <MenuItem key={r} value={r}>
                            {translate(r)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              ))
            )}
            {pendingInvites
              .filter((email) => !members.some((u) => u.email === email))
              .map((email) => (
                <Box
                  key={email}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    py: 1,
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                    {email[0].toUpperCase()}
                  </Avatar>
                  <Typography sx={{ flexGrow: 1 }}>{email}</Typography>
                  <Chip label={translate("Pending")} size="small" />
                </Box>
              ))}
          </Box>

          {/* <Box sx={{ mb: 2, bgcolor: "white", p: 2, borderRadius: 2 }}> */}
          <Box
            sx={{ mb: 2, bgcolor: "background.paper", p: 2, borderRadius: 2 }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                {translate("ProjectsList")}{" "}
                <span
                  style={{
                    color:
                      projects.length >= (isPremium ? Infinity : FREE_PROJECT_LIMIT)
                        ? "#d32f2f"
                        : "#2e7d32",
                    fontWeight: 700,
                  }}
                >
                  ({projects.length}/{isPremium ? "∞" : FREE_PROJECT_LIMIT})
                </span>
                {!isPremium && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => navigate("/app/gotopremium")}
                    sx={{
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 700,
                      px: 1.5,
                      minHeight: "28px",
                    }}
                  >
                    Passer Premium
                  </Button>
                )}
              </Typography>

              {role === "Admin" && (
                <Button
                  size="small"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={() => {
                    if (!canCreateAnotherProject) {
                      showCustomToast(
                        translate("PremiumNeededForMoreProjects"),
                        "error"
                      );
                      return;
                    }
                    setOpenAddProjModal(true);
                  }}
                >
                  {translate("AddAProject")}
                </Button>
              )}
            </Box>
            {loadingProjects ? (
              <Typography>{translate("Loading")}…</Typography>
            ) : (
              projects.map((p) => (
                <Box
                  key={p.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1,
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography>{p.name}</Typography>
                  {role === "Admin" && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setProjectToEdit(p);
                        setEditedProjMembers(
                          members.filter((u) => p.memberIds.includes(u.id))
                        );
                        setOpenEditProjModal(true);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))
            )}
          </Box>
          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Button
              variant="contained"
              color="error"
              onClick={async () => {
                await logOut();
                navigate("/login");
              }}
            >
              {translate("Signout")}
            </Button>
          </Box>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => setContactOpen(true)}
              sx={{ mt: 1 }}
            >
              {translate("ContactUs") || "Contact us"}
            </Button>
          </div>
        </Box>
        <Dialog
          open={openInviteDialog}
          onClose={() => setOpenInviteDialog(false)}
        >
          <DialogTitle>{translate("InviteUser")}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label={translate("EmailAddress")}
              type="email"
              fullWidth
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel>{translate("Role")}</InputLabel>
              <Select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                label={translate("Role")}
              >
                {ROLES.map((r) => (
                  <MenuItem key={r} value={r}>
                    {translate(r)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="dense"
              label={translate("OptionalMessage")}
              multiline
              rows={3}
              fullWidth
              value={inviteMessage}
              onChange={(e) => setInviteMessage(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenInviteDialog(false)}>
              {translate("Cancel")}
            </Button>
            <Button
              onClick={handleSendInvite}
              variant="contained"
              disabled={inviteLoading}
            >
              {inviteLoading ? (
                <CircularProgress size={16} />
              ) : (
                translate("SendInvite")
              )}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openAddProjModal}
          onClose={() => setOpenAddProjModal(false)}
        >
          <DialogTitle>{translate("AddProject")}</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label={translate("ProjectName")}
              fullWidth
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
            />

            <Autocomplete
              multiple
              options={members}
              getOptionLabel={(u) => u.name || u.email}
              value={selectedNewProjMembers}
              onChange={(_, v) =>
                setSelectedNewProjMembers(!isPremium ? v.slice(0, 3) : v)
              }
              getOptionDisabled={(option) =>
                !isPremium &&
                selectedNewProjMembers.length >= 3 &&
                !selectedNewProjMembers.some((u) => u.id === option.id)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translate("ProjectMembers")}
                  helperText={
                    !isPremium ? translate("Max3CollaboratorsHint") : ""
                  }
                />
              )}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAddProjModal(false)}>
              {translate("Cancel")}
            </Button>
            {/* <Button onClick={handleCreateProject} variant="contained"> */}
            <Button
              onClick={handleCreateProject}
              variant="contained"
              disabled={!canCreateAnotherProject}
              title={
                !canCreateAnotherProject
                  ? translate("PremiumNeededForMoreProjects")
                  : ""
              }
            >
              {translate("Create")}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={contactOpen} onClose={() => setContactOpen(false)}>
          <DialogTitle>{translate("ContactUs") || "Contact us"}</DialogTitle>
          <DialogContent>
            <TextField
              label={translate("EmailAddress") || "Email"}
              type="email"
              fullWidth
              margin="dense"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
            />
            <TextField
              label={translate("Subject") || "Subject"}
              fullWidth
              margin="dense"
              value={contactSubject}
              onChange={(e) => setContactSubject(e.target.value)}
            />
            <TextField
              label={translate("Message") || "Message"}
              fullWidth
              multiline
              rows={4}
              margin="dense"
              value={contactMessage}
              onChange={(e) => setContactMessage(e.target.value)}
            />
            {/* honeypot anti-bot */}
            <input
              type="text"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-5000px",
                height: 0,
                width: 0,
                opacity: 0,
                pointerEvents: "none",
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setContactOpen(false)}>
              {translate("Cancel") || "Cancel"}
            </Button>
            <Button
              variant="contained"
              disabled={contactLoading || !contactMessage.trim()}
              onClick={async () => {
                setContactLoading(true);
                try {
                  await sendContactMessage({
                    subject: contactSubject,
                    message: contactMessage,
                    fromEmail: contactEmail,
                    honeypot,
                  });
                  showCustomToast(
                    translate("MessageSent") || "Message sent",
                    "success"
                  );
                  setContactOpen(false);
                  setContactSubject("");
                  setContactMessage("");
                  setHoneypot("");
                  setContactEmail(currentUser?.email || "");
                } catch (e) {
                  showCustomToast(
                    translate("MessageFailed") || "Failed to send message",
                    "error"
                  );
                } finally {
                  setContactLoading(false);
                }
              }}
            >
              {contactLoading ? (
                <CircularProgress size={18} />
              ) : (
                translate("Send") || "Send"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <Modal
          open={openEditProjModal}
          onClose={() => setOpenEditProjModal(false)}
        >
          <Slide direction="left" in={openEditProjModal}>
            <Box
              sx={{
                position: "fixed",
                top: "5%",
                right: 0,
                width: "80%",
                height: "90%",
                bgcolor: "background.paper",
                boxShadow: 24,
                p: 2,
              }}
            >
              <Typography variant="h6">
                {translate("EditProject")} – {projectToEdit?.name}
              </Typography>

              <Autocomplete
                multiple
                options={members}
                getOptionLabel={(u) => u.name || u.email}
                value={editedProjMembers}
                onChange={(_, v) =>
                  setEditedProjMembers(!isPremium ? v.slice(0, 3) : v)
                }
                getOptionDisabled={(option) =>
                  !isPremium &&
                  editedProjMembers.length >= 3 &&
                  !editedProjMembers.some((u) => u.id === option.id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={translate("ProjectMembers")}
                    helperText={
                      !isPremium ? translate("Max3CollaboratorsHint") : ""
                    }
                  />
                )}
                sx={{ mt: 2 }}
              />

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button onClick={() => setOpenEditProjModal(false)}>
                  {translate("Cancel")}
                </Button>
                <Button onClick={handleEditProject} variant="contained">
                  {translate("Save")}
                </Button>
              </Box>
            </Box>
          </Slide>
        </Modal>
        <Dialog
          open={confirmDeleteUser}
          onClose={() => setConfirmDeleteUser(false)}
        >
          <DialogTitle>
            {translate("ConfirmDeleteUser", {
              name: userToDelete?.name || userToDelete?.email,
            })}
          </DialogTitle>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteUser(false)}>
              {translate("Cancel")}
            </Button>
            <Button color="error" onClick={handleDeleteUser}>
              {translate("Delete")}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={privacyOpen}
          onClose={() => setPrivacyOpen(false)}
          fullScreen={fullScreenPrivacy}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {translate("PrivacyPolicyTitle") || "Privacy Policy"}
          </DialogTitle>
          <DialogContent dividers>
            <PrivacyPolicy />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPrivacyOpen(false)}>
              {translate("Close") || "Close"}
            </Button>
          </DialogActions>
        </Dialog>

        <ToastContainer position="top-center" autoClose={1000} theme="dark" />
      </Box>
    </ThemeProvider>
  );
}
